/**
 * A aba Fechamento: o mês aberto em indicadores, tabelas por categoria e o
 * gráfico trimestral, pronto para imprimir.
 *
 * Papel: reproduz o `#tab-fechamento` da origem (l. 559–715) e
 * `renderFechamento` (l. 3147–3205): o cabeçalho com "Dashboard de
 * Fechamento" e o mês de referência, os quatro indicadores, "Saídas por
 * Categoria" à esquerda e, à direita, "Entradas por Categoria", "Movimentação
 * Bancária" (só com movimentação paga no mês), os três cartões da parte 5 e o
 * gráfico; embaixo, "Imprimir / Exportar PDF".
 *
 * Como usar: o mês aberto é da página, que o passa em `competencia` e o muda
 * por `onMudarMes`; a aba lê o Fechamento sozinha. Os cartões "Folha de
 * Pagamento", "Lançamentos de Outros Pagamentos" e "Pagamentos por Outros
 * Bancos", e o "Importar Folha XLSX" com o status, são **posições**: chegam
 * prontos pelos slots, e o slot vazio não ocupa espaço. A ordem da coluna da
 * direita é a da origem (l. 607–703) e não deverá mudar.
 *
 * No papel (marca `imprimindo-fechamento`, página `folha-a4-paisagem`): a
 * folha de 297 mm com 5 mm de respiro, o relatório com 1700 px e
 * `zoom:0.638`, o cabeçalho de impressão no alto, os indicadores em quatro
 * colunas com 8 px, as duas colunas lado a lado alinhadas em cima, e nada do
 * cabeçalho de tela nem das ações (l. 204–293). A quebra de 960 px é
 * `tela-estreita:`, que não vale no papel.
 */
import { type Fechamento } from '@concierge/shared';
import { IconPrinter } from '@tabler/icons-react';
import { type ReactNode } from 'react';

import { useImpressao } from '@/shared/embutido';
import { formatarReais, rotuloDoMes } from '../formato';
import { useCasaDoCaixa, useFechamento } from '../hooks/usar-financas';
import { CabecalhoDeImpressao } from './cabecalho-de-impressao';
import { GraficoTrimestral } from './grafico-trimestral';
import { Botao } from './kit/botao';
import { CabecalhoDoCartao, Cartao, CorpoDoCartao, TituloDoCartao } from './kit/cartao';
import { CartaoKpi } from './kit/cartao-kpi';
import { NavegacaoDoMes } from './kit/navegacao-do-mes';
import { type SituacaoDaTabela, TabelaPorCategoria } from './tabela-por-categoria';

/** A marca que liga o CSS de impressão do Fechamento. */
const MARCA_DE_IMPRESSAO = 'imprimindo-fechamento';

/** A tabela do mês de uma casa sem caixa. */
const TABELA_SEM_CAIXA = { total: 0, categorias: [] };

interface AbaFechamentoProps {
  /** O mês aberto, em `AAAA-MM`. */
  competencia: string;
  /** Anda o mês aberto: -1 pela seta ‹, +1 pela ›. */
  onMudarMes: (meses: number) => void;
  /** O cartão "Folha de Pagamento <sigla>", da parte 5. */
  folhaDePagamento?: ReactNode;
  /** O cartão "Lançamentos de Outros Pagamentos", da parte 5. */
  outrosPagamentos?: ReactNode;
  /** O cartão "Pagamentos por Outros Bancos", da parte 5. */
  pagamentosPorOutrosBancos?: ReactNode;
  /** O "Importar Folha XLSX" com o status ao lado, da parte 5. */
  importarFolha?: ReactNode;
}

/**
 * O cartão de uma tabela por categoria, com o total no cabeçalho.
 *
 * @param titulo - O título do cartão.
 * @param total - O total já escrito, ou nulo enquanto carrega.
 * @param children - A tabela.
 * @returns O cartão.
 */
function CartaoDeTabela({
  titulo,
  total,
  children,
}: {
  titulo: string;
  total: string | null;
  children: ReactNode;
}) {
  return (
    <Cartao>
      <CabecalhoDoCartao>
        <TituloDoCartao>{titulo}</TituloDoCartao>
        <TituloDoCartao className="mr-12 tabular-nums">{total}</TituloDoCartao>
      </CabecalhoDoCartao>
      <CorpoDoCartao className="px-0 py-2">{children}</CorpoDoCartao>
    </Cartao>
  );
}

/**
 * A aba Fechamento.
 *
 * @param competencia - O mês aberto.
 * @param onMudarMes - Anda o mês aberto.
 * @param folhaDePagamento - O slot da parte 5 depois de Movimentação.
 * @param outrosPagamentos - O slot da parte 5 depois da folha.
 * @param pagamentosPorOutrosBancos - O slot da parte 5 depois do gráfico.
 * @param importarFolha - O slot da parte 5 depois de "Imprimir / Exportar PDF".
 * @returns O relatório do mês.
 */
export function AbaFechamento({
  competencia,
  onMudarMes,
  folhaDePagamento,
  outrosPagamentos,
  pagamentosPorOutrosBancos,
  importarFolha,
}: AbaFechamentoProps) {
  const casa = useCasaDoCaixa();
  const consulta =
    casa.residenciaId === null ? null : { residenciaId: casa.residenciaId, competencia };
  const fechamento = useFechamento(consulta);
  const imprimir = useImpressao(MARCA_DE_IMPRESSAO);

  const semCaixa = casa.situacao === 'pronta' && casa.residenciaId === null;
  const dados: Fechamento | undefined = fechamento.data;

  let situacao: SituacaoDaTabela = 'pronta';
  let tentarDeNovo: () => void = () => void fechamento.refetch();
  if (casa.situacao !== 'pronta') {
    situacao = casa.situacao;
    tentarDeNovo = casa.tentarDeNovo;
  } else if (!semCaixa && dados === undefined) {
    situacao = fechamento.isError ? 'falhou' : 'carregando';
  }

  /**
   * Escreve um indicador, vazio enquanto o Fechamento não chega.
   *
   * @param valor - O valor do mês, quando há.
   * @returns O valor escrito, ou nulo.
   */
  function kpi(valor: number | undefined): string | null {
    if (valor !== undefined) return formatarReais(valor);
    return semCaixa ? formatarReais(0) : null;
  }

  const rotulo = rotuloDoMes(competencia);
  const saldoFinalNegativo = dados !== undefined && Math.round(dados.saldoFinal * 100) < 0;

  return (
    <div className="print:[body[data-impressao=imprimindo-fechamento]_&]:w-[297mm] print:[body[data-impressao=imprimindo-fechamento]_&]:bg-superficie print:[body[data-impressao=imprimindo-fechamento]_&]:p-[5mm] print:[body[data-impressao=imprimindo-fechamento]_&]:[page:folha-a4-paisagem] print:[body[data-impressao=imprimindo-fechamento]_&]:[print-color-adjust:exact] print:[body[data-impressao=imprimindo-fechamento]_&_*]:[print-color-adjust:exact]">
      <div className="print:[body[data-impressao=imprimindo-fechamento]_&]:w-[1700px] print:[body[data-impressao=imprimindo-fechamento]_&]:max-w-[1700px] print:[body[data-impressao=imprimindo-fechamento]_&]:[zoom:0.638]">
        <CabecalhoDeImpressao rotulo={rotulo} />

        <div className="mb-[18px] flex items-center justify-between print:[body[data-impressao=imprimindo-fechamento]_&]:hidden">
          <h2 className="font-outfit text-[22px] font-light tracking-[0.08em] text-foreground uppercase">
            Dashboard de Fechamento
          </h2>
          <NavegacaoDoMes
            rotulo={rotulo}
            onAnterior={() => onMudarMes(-1)}
            onProximo={() => onMudarMes(1)}
          />
        </div>

        <div className="mb-4 grid grid-cols-4 gap-3.5 tela-estreita:grid-cols-2 print:[body[data-impressao=imprimindo-fechamento]_&]:break-inside-avoid print:[body[data-impressao=imprimindo-fechamento]_&]:gap-2">
          <CartaoKpi
            rotulo="Saídas"
            indicador="saida"
            valor={kpi(dados?.saidas)}
            corDoValor="perigo"
            className="print:[body[data-impressao=imprimindo-fechamento]_&]:px-3 print:[body[data-impressao=imprimindo-fechamento]_&]:py-2.5"
          />
          <CartaoKpi
            rotulo="Entradas"
            indicador="entrada"
            valor={kpi(dados?.entradas)}
            corDoValor="sucesso"
            className="print:[body[data-impressao=imprimindo-fechamento]_&]:px-3 print:[body[data-impressao=imprimindo-fechamento]_&]:py-2.5"
          />
          <CartaoKpi
            rotulo="Saldo Inicial"
            indicador="inicial"
            valor={kpi(dados?.saldoInicial)}
            corDoValor="normal"
            className="print:[body[data-impressao=imprimindo-fechamento]_&]:px-3 print:[body[data-impressao=imprimindo-fechamento]_&]:py-2.5"
          />
          <CartaoKpi
            rotulo="Saldo Final"
            indicador="final"
            valor={kpi(dados?.saldoFinal)}
            corDoValor={saldoFinalNegativo ? 'perigo' : 'normal'}
            className="print:[body[data-impressao=imprimindo-fechamento]_&]:px-3 print:[body[data-impressao=imprimindo-fechamento]_&]:py-2.5"
          />
        </div>

        <div className="grid grid-cols-2 gap-4 tela-estreita:grid-cols-1 [&>*]:min-w-0 print:[body[data-impressao=imprimindo-fechamento]_&]:items-start print:[body[data-impressao=imprimindo-fechamento]_&]:gap-2.5">
          <CartaoDeTabela
            titulo="Saídas por Categoria"
            total={dados === undefined ? null : formatarReais(dados.saidasPorCategoria.total)}
          >
            <TabelaPorCategoria
              key={competencia}
              tabela={dados?.saidasPorCategoria ?? (semCaixa ? TABELA_SEM_CAIXA : null)}
              situacao={situacao}
              onTentarDeNovo={tentarDeNovo}
            />
          </CartaoDeTabela>

          <div>
            <CartaoDeTabela
              titulo="Entradas por Categoria"
              total={
                dados === undefined ? null : formatarReais(dados.entradasPorCategoria.total)
              }
            >
              <TabelaPorCategoria
                key={competencia}
                tabela={dados?.entradasPorCategoria ?? (semCaixa ? TABELA_SEM_CAIXA : null)}
                situacao={situacao}
                onTentarDeNovo={tentarDeNovo}
                corDoValor="sucesso"
              />
            </CartaoDeTabela>

            {dados?.movimentacao != null && (
              <div className="break-inside-avoid">
                <CartaoDeTabela
                  titulo="Movimentação Bancária"
                  total={formatarReais(dados.movimentacao.total)}
                >
                  <TabelaPorCategoria
                    key={competencia}
                    tabela={dados.movimentacao}
                    situacao="pronta"
                    onTentarDeNovo={tentarDeNovo}
                    corDoValor="movimentacao"
                  />
                </CartaoDeTabela>
              </div>
            )}

            {folhaDePagamento}
            {outrosPagamentos}
            <GraficoTrimestral trimestres={dados?.trimestres ?? null} />
            {pagamentosPorOutrosBancos}
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 print:[body[data-impressao=imprimindo-fechamento]_&]:hidden">
          <Botao variante="fantasma" tamanho="sm" onClick={() => void imprimir()}>
            <IconPrinter size={13} aria-hidden="true" /> Imprimir / Exportar PDF
          </Botao>
          {importarFolha}
        </div>
      </div>
    </div>
  );
}
